import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";

export interface SimulatorData {
  email: string;
  leadId: string;
  leiloeiro: string;
  fotos: File[];
  fotoUrls: string[];
  marcaId: string;
  marcaNome: string;
  modeloId: string;
  modeloNome: string;
  anoCode: string;
  anoNome: string;
  km: number;
  observacoes: string;
  valorFipe: number;
  valorFipeFormatado: string;
  // Result fields
  analysisResult?: AnalysisResult;
}

export interface AnalysisResult {
  score_condicao: number;
  nivel_dano: "leve" | "moderado" | "grave";
  mapa_calor: Record<string, number>;
  analise_tecnica: string;
  recomendacao: string;
  valor_medio_mercado?: number;
  custo_reparo_estimado?: number;
  lance_seguro_sugerido?: number;
  margem_lucro_estimada?: number;
  detalhes_reparo?: { peca: string; custo: number; url?: string; is_restricted?: boolean }[];
  servicos_logistica?: { item: string; custo: number; url?: string; is_restricted?: boolean }[];
  fontes_olx?: { titulo: string; preco: number; url?: string; is_restricted?: boolean }[];
  fontes_olx_restricted_count?: number;
  detalhes_reparo_restricted_count?: number;
  mao_de_obra_info?: string;
  analise_fotos_concluida?: boolean;
}

export interface UserLocation {
  cidade: string;
  estado: string;
}

export interface PriceResult {
  desconto: number;
  valorEstimado: number;
  faixaMin: number;
  faixaMax: number;
  kmAlert?: string;
  regionalFactor: number;
}

export function calcularPreco(
  valorFipe: number,
  scoreCondicao: number,
  km: number = 0,
  ano: number = 0,
  estado: string = ""
): PriceResult {
  let regionalFactor = 1.0;

  // Regional Liquidity Factor (Refined)
  const highLiquidity = ["SP", "RJ", "MG", "PR", "SC", "RS"]; // Sul e Sudeste (Core market)
  const medLiquidity = ["DF", "GO", "ES", "BA", "CE", "PE"];    // Centros regionais fortes
  const lowLiquidity = ["AC", "RR", "AP", "AM", "TO", "RO"];    // Áreas com logística complexa

  if (highLiquidity.includes(estado.toUpperCase())) regionalFactor = 1.03;
  else if (medLiquidity.includes(estado.toUpperCase())) regionalFactor = 1.0;
  else if (lowLiquidity.includes(estado.toUpperCase())) regionalFactor = 0.92;
  else regionalFactor = 0.97; // Default para outros estados

  // Inversão corrigida: Score 100 (perfeito) -> 0% desc adicional. Score 0 (ruim) -> 40% desc adicional.
  const baseDesconto = (1 - (scoreCondicao / 100)) * 0.4;

  // KM Intelligence
  let kmAlert = "";
  const currentYear = new Date().getFullYear();
  const age = Math.max(1, currentYear - ano);
  const avgKmPerYear = km / age;

  if (avgKmPerYear < 5000 && age > 3) {
    kmAlert = "KM Atipicamente Baixa (Possível Fraude)";
  } else if (avgKmPerYear > 30000) {
    kmAlert = "Uso Severo/Frota Detectado";
  }

  const valorEstimado = valorFipe * (1 - baseDesconto) * regionalFactor;
  const faixaMin = valorEstimado * 0.85; // Ampliado para segurança
  const faixaMax = valorEstimado * 1.05;

  return {
    desconto: baseDesconto,
    valorEstimado,
    faixaMin,
    faixaMax,
    kmAlert,
    regionalFactor
  };
}

/**
 * Calcula o lance seguro sugerido com base nos dados disponíveis.
 * Abordagem ULTRA-CONSERVADORA: deduz reparos + margem de lucro agressiva de 25%.
 */
export function calcularLanceSeguro(
  valorEstimado: number,
  custoReparo: number = 0,
  margemSeguranca: number = 0.30 // Definido para 30% (Regra dos 70% do lance máximo)
): { lance: number; margem: number; breakdown: LanceBreakdown } {
  const valorPosReparo = valorEstimado - (custoReparo * 1.2); // 20% buffer no reparo
  const deducaoSeguranca = valorPosReparo * margemSeguranca;
  const lance = Math.max(0, valorPosReparo - deducaoSeguranca);
  const margem = valorEstimado > 0 ? (valorEstimado - lance) / valorEstimado : 0;

  return {
    lance,
    margem,
    breakdown: {
      valorMercado: valorEstimado,
      custoReparo: custoReparo * 1.2,
      margemSeguranca: deducaoSeguranca,
      lanceFinal: lance,
    },
  };
}

export interface LanceBreakdown {
  valorMercado: number;
  custoReparo: number;
  margemSeguranca: number;
  lanceFinal: number;
}

export function formatBRL(value: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export async function sendToWebhook(data: Record<string, unknown>): Promise<AnalysisResult> {
  // Mapeia o nome do leiloeiro de volta para o ID para buscar as regras
  const leiloeiroNome = data.leiloeiro as string;
  const leiloeiroMatch = LEILOEIROS.find(l => l.name.trim().toLowerCase() === leiloeiroNome?.trim().toLowerCase());
  const leiloeiroId = leiloeiroMatch?.id || "outro";

  if (import.meta.env.MODE === "development") {
    console.log("[WEBHOOK] Mapping:", {
      inputName: leiloeiroNome,
      foundId: leiloeiroId,
      foundMatch: leiloeiroMatch?.name
    });
  }

  // Busca regras do leiloeiro no banco se existirem
  const { data: rules } = await supabase
    .from("auctioneer_rules")
    .select("rules_text, pdf_url")
    .eq("leiloeiro_id", leiloeiroId)
    .maybeSingle();

  const payload = {
    ...data,
    modo: data.modo || "gratuito",
    auctioneer_rules: rules?.rules_text || "Sem regras específicas cadastradas para este leiloeiro.",
    auctioneer_pdf_url: rules?.pdf_url || null,
    timestamp: new Date().toISOString()
  };

  if (import.meta.env.MODE === "development") {
    console.log("[WEBHOOK] Rules Fetch:", {
      idUsed: leiloeiroId,
      hasRules: !!rules,
      pdfUrl: rules?.pdf_url
    });
  }

  const res = await fetch(
    "https://nbhzbstabtkjrhrwfhoj.supabase.co/functions/v1/simulator-webhook",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) throw new Error("Erro ao processar análise");
  return res.json();
}

export const LEILOEIROS = [
  { id: "sodre", name: "Sodré Santoro", icon: "Gavel" as const, oficial: true },
  { id: "copart", name: "Copart", icon: "Car" as const, oficial: true },
  { id: "vip", name: "VIP Leilões", icon: "Trophy" as const, oficial: true },
  { id: "superbid", name: "Superbid", icon: "Zap" as const, oficial: true },
  { id: "freitas", name: "Freitas Leiloeiro", icon: "Scale" as const, oficial: true },
  { id: "outro", name: "Outro leiloeiro", icon: "HelpCircle" as const, oficial: false },
] as const;

export const FOTO_SLOTS = [
  {
    id: "frente",
    label: "Frente",
    icon: "CarFront",
    orientacaoCamera: "Fotografe o veículo de frente. Inclua faróis, grade e para-choque completo.",
    orientacaoUpload: "Selecione a foto da frente do veículo com faróis, grade e para-choque visíveis.",
  },
  {
    id: "traseira",
    label: "Traseira",
    icon: "CarFront",
    orientacaoCamera: "Fotografe a parte traseira completa, incluindo lanternas e para-choque.",
    orientacaoUpload: "Selecione a foto traseira completa com lanternas e para-choque.",
  },
  {
    id: "lateral_esq",
    label: "Lateral Esquerda",
    icon: "CarFront",
    orientacaoCamera: "Capture toda a lateral esquerda, incluindo rodas, portas e colunas.",
    orientacaoUpload: "Selecione a foto da lateral esquerda com rodas, portas e colunas.",
  },
  {
    id: "lateral_dir",
    label: "Lateral Direita",
    icon: "CarFront",
    orientacaoCamera: "Capture toda a lateral direita do veículo, mostrando portas e retrovisores.",
    orientacaoUpload: "Selecione a foto da lateral direita com portas e retrovisores.",
  },
  {
    id: "diag_tras_esq",
    label: "Diagonal Traseira Esq.",
    icon: "CarFront",
    orientacaoCamera: "Fotografe a diagonal traseira esquerda, mostrando a traseira e lateral.",
    orientacaoUpload: "Selecione a foto diagonal traseira esquerda.",
  },
  {
    id: "diag_tras_dir",
    label: "Diagonal Traseira Dir.",
    icon: "CarFront",
    orientacaoCamera: "Fotografe a diagonal traseira direita, mostrando a traseira e lateral.",
    orientacaoUpload: "Selecione a foto diagonal traseira direita.",
  },
  {
    id: "painel",
    label: "Painel Interno",
    icon: "Gauge",
    orientacaoCamera: "Com as portas abertas, fotografe o painel completo. Inclua volante e instrumentos.",
    orientacaoUpload: "Selecione a foto do painel interno com volante e instrumentos.",
  },
  {
    id: "motor",
    label: "Motor",
    icon: "Cog",
    orientacaoCamera: "Com o capô aberto, fotografe o compartimento do motor completo.",
    orientacaoUpload: "Selecione a foto do compartimento do motor.",
  },
  {
    id: "portamalas",
    label: "Porta-malas",
    icon: "Package",
    orientacaoCamera: "Fotografe o porta-malas aberto, mostrando o espaço e estado interno.",
    orientacaoUpload: "Selecione a foto do porta-malas aberto.",
  },
  {
    id: "extras",
    label: "Adicionar mais...",
    icon: "Plus",
    orientacaoCamera: "Fotos extras de avarias ou detalhes que deseja destacar.",
    orientacaoUpload: "Fotos extras de avarias ou detalhes que deseja destacar.",
  },
] as const;

export const TOTAL_STEPS = 6;

export async function saveSimulation(
  data: Partial<SimulatorData>,
  result: AnalysisResult,
  location?: UserLocation
): Promise<string | null> {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) console.error("[AUTH] Error getting session for saveSimulation:", sessionError);
  
  const userId = session?.user?.id || null;
  let userPlan = "gratuito";
  
  if (userId) {
    const { data: profile } = await supabase
      .from("profile")
      .select("plan")
      .eq("user_id", userId)
      .single();
    if (profile?.plan) userPlan = profile.plan;
  }

  if (import.meta.env.MODE === "development") {
    console.log("[SIMULATOR] Saving simulation for user:", userId || "Anonymous", "Plan:", userPlan);
  }

  const price = calcularPreco(
    data.valorFipe || 0,
    result.score_condicao,
    data.km,
    parseInt(data.anoNome || "0"),
    location?.estado
  );

  const lanceCalc = calcularLanceSeguro(price.valorEstimado, result.custo_reparo_estimado);

  if (import.meta.env.MODE === "development") {
    console.log("[SIMULATOR] Payload to Insert:", {
      lead_id: data.leadId || "generated",
      user_id: userId,
      marca: data.marcaNome,
      modelo: data.modeloNome
    });
  }

  const { data: sim, error } = await supabase.from("simulacoes").insert({
    lead_id: data.leadId || null,
    user_id: userId,
    marca: data.marcaNome || "",
    modelo: data.modeloNome || "",
    ano: parseInt(data.anoNome || "0"),
    km: data.km || 0,
    valor_fipe: data.valorFipe || 0,
    leiloeiro: data.leiloeiro || "",
    observacoes: data.observacoes || "",
    imagens: data.fotoUrls || [],
    modo: userPlan,
    score_condicao: result.score_condicao,
    nivel_dano: result.nivel_dano,
    mapa_calor: result.mapa_calor,
    analise_tecnica: result.analise_tecnica,
    recomendacao: result.recomendacao,
    desconto_aplicado: price.desconto,
    valor_estimado: price.valorEstimado,
    faixa_min: price.faixaMin,
    faixa_max: price.faixaMax,
    km_alert: price.kmAlert || null,
    regional_factor: price.regionalFactor || 1,
    // Novos campos essenciais para o laudo PDF
    valor_medio_mercado: result.valor_medio_mercado || 0,
    custo_reparo_estimado: result.custo_reparo_estimado || 0,
    lance_seguro_sugerido: result.lance_seguro_sugerido || lanceCalc.lance,
    margem_lucro_estimada: result.margem_lucro_estimada || lanceCalc.margem,
    detalhes_reparo: result.detalhes_reparo || [],
    fontes_olx: result.fontes_olx || [],
    servicos_logistica: result.servicos_logistica || [],
    mao_de_obra_info: result.mao_de_obra_info || "",
  } as any).select("id").single();

  if (error) {
    if (import.meta.env.MODE === "development") {
      console.error("[SIMULATOR] Critical error saving simulation (full object):", JSON.stringify(error, null, 2));
    }
    toast.error(`Falha ao salvar: ${error.message || "Erro desconhecido"}`);
    return null;
  }
  
  if (import.meta.env.MODE === "development") {
    console.log("[SIMULATOR] Simulation saved successfully ID:", sim?.id);
  }
  toast.success("Análise persistida com sucesso!");
  return sim?.id || null;
}
